import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Search, ArrowRight, X, Check, Trash2,
  RefreshCw, ExternalLink, Filter, Eye,
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const STATUS_LABELS = {
  open:       { label: 'Abierto',     class: 'bg-amber-500/15 border-amber-500/40 text-amber-300' },
  dismissed:  { label: 'Descartado',  class: 'bg-zinc-500/15 border-zinc-500/40 text-zinc-300' },
  redirected: { label: 'Con redirect', class: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
};

const Url404HitsPage = () => {
  const [data, setData] = useState({ hits: [], total_open: 0, total_redirected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | open | dismissed | redirected
  const [search, setSearch] = useState('');
  const [redirectInputs, setRedirectInputs] = useState({}); // {path: 'value'}

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const r = await api.get('/admin/url-404-hits', { params });
      setData(r.data || { hits: [], total_open: 0, total_redirected: 0 });
    } catch (e) {
      toast.error('Error al cargar 404s');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const setRedirect = async (path, to) => {
    if (!to.trim()) {
      toast.error('Ingresá la URL destino');
      return;
    }
    try {
      await api.patch('/admin/url-404-hits', {
        path,
        status: 'redirected',
        redirect_to: to.trim(),
      });
      toast.success(`Redirect creado: ${path} → ${to}`);
      setRedirectInputs(prev => ({ ...prev, [path]: '' }));
      load();
    } catch (e) {
      toast.error('No se pudo crear el redirect');
    }
  };

  const setStatus = async (path, status) => {
    try {
      await api.patch('/admin/url-404-hits', { path, status });
      toast.success(`Estado actualizado: ${STATUS_LABELS[status]?.label || status}`);
      load();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const removeHit = async (path) => {
    if (!window.confirm(`¿Eliminar entrada "${path}"?`)) return;
    try {
      await api.post('/admin/url-404-hits/delete', { path });
      toast.success('Entrada eliminada');
      load();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const visibleHits = data.hits.filter(h =>
    !search.trim() || h.path.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 lg:p-8" data-testid="url-404-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="text-amber-400" size={22} />
              <h1 className="text-2xl lg:text-3xl font-black tracking-tight">URLs rotas (404)</h1>
            </div>
            <p className="text-zinc-500 text-sm">
              URLs que devuelven "página no encontrada". Convertilas en redirect para no perder tráfico.
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="border-zinc-700"
            data-testid="url-404-refresh-btn"
          >
            <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-zinc-500 text-xs uppercase tracking-wider">URLs únicas</p>
            <p className="text-2xl font-black">{data.hits.length}</p>
          </div>
          <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-4">
            <p className="text-amber-400 text-xs uppercase tracking-wider">Abiertos</p>
            <p className="text-2xl font-black text-amber-400">{data.total_open}</p>
          </div>
          <div className="bg-zinc-900 border border-emerald-500/30 rounded-xl p-4">
            <p className="text-emerald-400 text-xs uppercase tracking-wider">Con redirect</p>
            <p className="text-2xl font-black text-emerald-400">{data.total_redirected}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Hits en vista</p>
            <p className="text-2xl font-black">{data.total_hits_in_view || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={14} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrar por URL..."
              className="pl-9 bg-zinc-900 border-zinc-800"
              data-testid="url-404-search"
            />
          </div>
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
            {['all', 'open', 'redirected', 'dismissed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all rounded ${
                  statusFilter === s ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`url-404-filter-${s}`}
              >
                {s === 'all' ? 'Todos' : STATUS_LABELS[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-zinc-500">
            <RefreshCw className="animate-spin mx-auto mb-3" size={28} />
            <p>Cargando...</p>
          </div>
        ) : visibleHits.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Check size={42} className="mx-auto text-emerald-500 mb-3" />
            <p className="text-zinc-400">No hay URLs rotas{statusFilter !== 'all' ? ` (filtro: ${STATUS_LABELS[statusFilter]?.label || statusFilter})` : ''}.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="url-404-list">
            {visibleHits.map((h) => {
              const statusCfg = STATUS_LABELS[h.status] || STATUS_LABELS.open;
              return (
                <motion.div
                  key={h.path}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-all"
                  data-testid={`url-404-row-${encodeURIComponent(h.path)}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${statusCfg.class}`}>
                        {statusCfg.label}
                      </span>
                      <code className="text-sm text-white font-mono truncate" title={h.path}>{h.path}</code>
                      <a
                        href={h.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-500 hover:text-[#D4AF37] flex-shrink-0"
                        title="Abrir URL"
                      >
                        <ExternalLink size={13} />
                      </a>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      <span><Eye size={11} className="inline mr-1" />{h.count} hits</span>
                      {h.last_seen && (
                        <span>Última: {new Date(h.last_seen).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}</span>
                      )}
                    </div>
                  </div>

                  {h.redirect_to && (
                    <div className="flex items-center gap-2 mb-2 text-xs">
                      <ArrowRight size={12} className="text-emerald-400" />
                      <span className="text-zinc-500">Redirige a</span>
                      <code className="text-emerald-400 font-mono bg-emerald-500/5 px-1.5 py-0.5 rounded">{h.redirect_to}</code>
                    </div>
                  )}

                  {h.referrers?.length > 0 && (
                    <p className="text-[10px] text-zinc-600 mb-2 truncate">
                      Origen: {h.referrers.slice(0, 2).join(' · ')}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t border-zinc-800/50">
                    <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                      <Input
                        placeholder="Crear redirect a... (ej: /eventos)"
                        value={redirectInputs[h.path] || h.redirect_to || ''}
                        onChange={(e) => setRedirectInputs(prev => ({ ...prev, [h.path]: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                        data-testid={`url-404-redirect-input-${encodeURIComponent(h.path)}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => setRedirect(h.path, redirectInputs[h.path] ?? h.redirect_to ?? '')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                        data-testid={`url-404-redirect-btn-${encodeURIComponent(h.path)}`}
                      >
                        <ArrowRight size={12} className="mr-1" />
                        Aplicar
                      </Button>
                    </div>
                    {h.status !== 'dismissed' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setStatus(h.path, 'dismissed')}
                        className="text-zinc-400 hover:text-white h-8"
                      >
                        <X size={12} className="mr-1" />
                        Descartar
                      </Button>
                    )}
                    {h.status !== 'open' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setStatus(h.path, 'open')}
                        className="text-zinc-400 hover:text-white h-8"
                      >
                        Reabrir
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeHit(h.path)}
                      className="text-red-400 hover:text-red-300 h-8"
                      data-testid={`url-404-delete-${encodeURIComponent(h.path)}`}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Url404HitsPage;
